import type { TextChain } from '@/api/types/stowed-element'
import { chainContaining } from '@/lib/text-linking/text-chain-mutator'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'
import { isShapeBackedFrame } from '@/lib/text-linking/linkable-text-frame'

/**
 * Outcome of resolving a Ctrl+A chain select-all for `frameId`:
 * - `no-frame`: the id is not a linkable frame on this spread — the chain state and the shape list
 *   disagree, which the caller logs.
 * - `solo`: a perfectly healthy solo frame (no uid, no chains in scope, or not in a chain).
 * - `no-members`: the chain has no member mounted on this spread, which the caller logs.
 * - `members`: the shape-backed member ids to seed a cross-frame text selection over.
 */
export type ChainSelectMembersResolution =
  | { kind: 'no-frame' }
  | { kind: 'solo' }
  | { kind: 'no-members' }
  | { kind: 'members'; shapeMemberIds: string[] }

/** The decision tree behind `handleSelectChainMembers`: which frames a chain select-all covers. */
export function resolveChainSelectMembers(
  frames: LinkableTextFrame[],
  frameId: string,
  linkedTextChains: TextChain[] | undefined
): ChainSelectMembersResolution {
  const frame = frames.find((f) => f.id === frameId)

  if (!frame) {
    return { kind: 'no-frame' }
  }

  if (frame.uid == null) {
    return { kind: 'solo' }
  }

  if (!linkedTextChains || linkedTextChains.length === 0) {
    return { kind: 'solo' }
  }

  const chain = chainContaining(linkedTextChains, String(frame.uid))

  if (!chain) {
    return { kind: 'solo' }
  }

  const frameByUid = new Map(
    frames.filter((f) => f.uid != null).map((f) => [String(f.uid), f] as const)
  )
  // No `locked` or `frozen` filter here, deliberately (WMPCM-9728). A cross-frame selection
  // is a READ over the story, and reading a protected frame is always legal — dropping a
  // locked member is what left its text out of the chain copy, and on a two-frame chain it
  // took the member count below `seedChainTextSelectionOverFrames`' minimum of 2, so Ctrl+A
  // did nothing at all (the editor has already `preventDefault()`ed the native select-all).
  // The write side is where protection belongs: `mutateChainTextSelection`
  // (`shape-manager/hooks/use-chain-text-clipboard.ts`) refuses a destructive edit spanning a locked member and skips it on
  // a restyle. This also makes the two seeders agree — the drag-grown one
  // (`extendChainTextSelection`) has never filtered either flag.
  //
  // In particular NOT `isObjectEligibleForSelectAll`: that encodes SC-CP-03/WMPCM-8387, where
  // a selection is about to be transformed, and it also excludes `frozen`, which dropped every
  // member of a frozen chain (WMPCM-9324).
  const members = chain.textBox
    .map((tb) => frameByUid.get(tb.uid))
    .filter((f): f is LinkableTextFrame => f != null)

  if (members.length === 0) {
    return { kind: 'no-members' }
  }

  return { kind: 'members', shapeMemberIds: members.filter(isShapeBackedFrame).map((f) => f.id) }
}
